import { useCallback, useState } from 'react';
import { getAssetUploadUrl, putFileWithProgress } from './api';

// Client-side mirror of the server's accept lists + caps (server/lib/assetStorage.ts).
// Keep these in sync — the server re-validates (and the signed upload URL carries an
// x-goog-content-length-range header GCS enforces), but failing fast here saves a
// round-trip and gives a clearer message.
export const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;
export const ALLOWED_VIDEO_TYPES = ['video/mp4', 'video/quicktime'] as const; // D3.4
export const MAX_IMAGE_BYTES = 10 * 1024 * 1024; // 10 MB
export const MAX_VIDEO_BYTES = 1024 * 1024 * 1024; // 1 GB (D3.4)
// Back-compat alias (image cap).
export const MAX_ASSET_BYTES = MAX_IMAGE_BYTES;

export type AssetKind = 'image' | 'video';

// The durable handle returned to the caller (url + Storage path). Named for legacy
// reasons; carries either an image or a video depending on `kind`.
export type UploadedImage = { imageUrl: string; imagePath: string };

const MB = 1024 * 1024;

export function validateFile(file: File, kind: AssetKind): string | null {
  if (kind === 'video') {
    if (!ALLOWED_VIDEO_TYPES.includes(file.type as (typeof ALLOWED_VIDEO_TYPES)[number])) {
      return 'Unsupported format. Upload an MP4 video.';
    }
    if (file.size > MAX_VIDEO_BYTES) {
      return `File is too large (max ${Math.round(MAX_VIDEO_BYTES / MB)} MB).`;
    }
    return null;
  }
  if (!ALLOWED_IMAGE_TYPES.includes(file.type as (typeof ALLOWED_IMAGE_TYPES)[number])) {
    return 'Unsupported format. Upload a JPEG, PNG, or WebP image.';
  }
  if (file.size > MAX_IMAGE_BYTES) {
    return `File is too large (max ${Math.round(MAX_IMAGE_BYTES / MB)} MB).`;
  }
  return null;
}

export type UseAssetUpload = {
  upload: (file: File, kind: AssetKind) => Promise<UploadedImage | null>;
  uploading: boolean;
  progress: number; // 0–100
  error: string | null;
  reset: () => void;
};

// Reusable upload hook for the Feed + Story asset slots (D3.4). Validates client-
// side by kind, fetches a signed URL, PUTs the bytes with progress, and returns the
// durable { imageUrl, imagePath }. Errors are surfaced (never thrown to the caller).
export function useAssetUpload(accountId: string): UseAssetUpload {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const reset = useCallback(() => {
    setUploading(false);
    setProgress(0);
    setError(null);
  }, []);

  const upload = useCallback(
    async (file: File, kind: AssetKind): Promise<UploadedImage | null> => {
      const invalid = validateFile(file, kind);
      if (invalid) {
        setError(invalid);
        return null;
      }
      setError(null);
      setUploading(true);
      setProgress(0);
      try {
        const { uploadUrl, imageUrl, imagePath, requiredHeaders } = await getAssetUploadUrl({
          accountId,
          contentType: file.type,
          originalFilename: file.name,
        });
        await putFileWithProgress(uploadUrl, file, requiredHeaders, setProgress);
        setProgress(100);
        return { imageUrl, imagePath };
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Upload failed. Try again.');
        return null;
      } finally {
        setUploading(false);
      }
    },
    [accountId],
  );

  return { upload, uploading, progress, error, reset };
}
